import { useEffect, useState } from "react";
import { Loader, Modal } from "rsuite";

import { Divider, Table, Tag, Button, Popconfirm } from "antd";
import { useNotification } from "../../../utils/notificationProvider";
import { appCRUD } from "../../../actions/app";
import { char } from "../../../utils";
import { FilePdfOutlined, FileExcelOutlined, PlayCircleOutlined } from "@ant-design/icons";

export const ExamTimetableView = (props) => {
  const { open, setOpen, dataId } = props;

  const openNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (dataId && open) {
      setLoading(true);
      setTimeout(() => {
        appCRUD
          .loadExamSession(dataId)
          .then((resp) => {
            const results = resp.data.results || resp.data || [];
            setData(Array.isArray(results) ? results : []);
          })
          .catch((error) => {
            console.error("Error loading exam sessions:", error);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
    }
  }, [dataId, open]);

  const handleGenerate = () => {
    setGenerating(true);
    appCRUD
      .generateExamTimetable(dataId)
      .then((resp) => {
        openNotification("success", "Success", "Exam timetable generated successfully!");
        // Reload data
        appCRUD
          .loadExamSession(dataId)
          .then((resp) => {
            const results = resp.data.results || resp.data || [];
            setData(Array.isArray(results) ? results : []);
          });
      })
      .catch((error) => {
        openNotification(
          "error",
          "Error",
          error.response?.data?.error || "Failed to generate timetable"
        );
      })
      .finally(() => {
        setGenerating(false);
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    return timeStr.slice(0, 5);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'exam_date',
      key: 'exam_date',
      render: (text) => formatDate(text),
      sorter: (a, b) => new Date(a.exam_date) - new Date(b.exam_date),
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, record) => `${formatTime(record.start_time)} - ${formatTime(record.end_time)}`,
    },
    {
      title: 'Course',
      dataIndex: 'course',
      key: 'course',
    },
    {
      title: 'Course Name',
      dataIndex: 'course_name',
      key: 'course_name',
      render: (text) => char.toTitleCase(text || ''),
    },
    {
      title: 'Programme',
      dataIndex: 'programme',
      key: 'programme',
      render: (_, record) => record.programme?.code || 'N/A',
    },
    {
      title: 'Year',
      dataIndex: 'study_year',
      key: 'study_year',
      width: 60,
    },
    {
      title: 'Type',
      dataIndex: 'exam_type',
      key: 'exam_type',
      render: (text) => (
        <Tag color={text === 'WRITTEN' ? 'blue' : text === 'PRACTICAL' ? 'green' : 'orange'}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Students',
      dataIndex: 'expected_students',
      key: 'expected_students',
      width: 80,
    },
  ];

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)} size="full">
        <Modal.Header>
          <Modal.Title>Examination Timetable</Modal.Title>
        </Modal.Header>
        <Divider />
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "50vh",
                      overflow: "hidden",
                    }}
                  >
                    <Loader
                      backdrop
                      size="md"
                      speed="slow"
                      content="Loading exam sessions..."
                      vertical
                    />
                  </div>
                ) : data.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">No exam sessions found.</p>
                    <Popconfirm
                      title="Generate Exam Timetable"
                      description="This will auto-generate the exam schedule based on enrollments."
                      onConfirm={handleGenerate}
                      okText="Generate"
                      cancelText="Cancel"
                    >
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        loading={generating}
                        size="large"
                      >
                        Generate Exam Timetable
                      </Button>
                    </Popconfirm>
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    dataSource={data.map((item, idx) => ({ ...item, key: idx }))}
                    pagination={{ pageSize: 20 }}
                    size="small"
                    bordered
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Divider style={{ marginTop: -10 }} />
        <Modal.Footer>
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-6 col-sm-12 d-flex justify-content-start">
                {data.length > 0 && (
                  <>
                    <button
                      onClick={() => window.open(appCRUD.getExamExportPDFUrl(dataId), '_blank')}
                      type="button"
                      className="btn btn-soft-primary me-2"
                    >
                      <FilePdfOutlined className="me-2" />
                      PDF
                    </button>
                    <button
                      onClick={() => window.open(appCRUD.getExamExportExcelUrl(dataId), '_blank')}
                      type="button"
                      className="btn btn-soft-success me-2"
                    >
                      <FileExcelOutlined className="me-2" />
                      Excel
                    </button>
                  </>
                )}
              </div>
              <div className="col-md-6 col-sm-12 d-flex justify-content-end">
                <button
                  onClick={() => setOpen(false)}
                  type="button"
                  className="btn btn-soft-danger me-3"
                >
                  <i className="ri-close-circle-line align-middle fs-16 me-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
